import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { UseDataProps } from './types';
import { useStore } from '30.quickConnect.Stores';
import { DeclarationViewer } from '90.quickConnect.Models/models';
import { DECLARATION } from '10.quickConnect.app/routes/paths';
import { useIsMobile } from '80.quickConnect.Core/hooks';
import { TreeDataItem } from '30.quickConnect.Stores/helpers/type';
import useOpenDraft from '10.quickConnect.app/components/shared/Dialogs/OuSwitchPrompt/useOpenDraft';

const useData = (declaration: TreeDataItem): UseDataProps => {
  const {
    DeclarationStore: { deleteDraftDeclarationAsync },
  } = useStore();

  const { t } = useTranslation('forms');
  const isMobile = useIsMobile();
  const { id, formId } = declaration as DeclarationViewer;
  // On ajoute des hooks
  const link = useMemo(() => DECLARATION.replace(':formId', formId).replace(':declarationId', id), [formId, id]);

  const [contextMenu, setContextMenu] = useState<{
    mouseX: number;
    mouseY: number;
  } | null>(null);

  const { openDraft, promptProps } = useOpenDraft();

  const openDeclaration = useCallback(async () => {
    await openDraft(declaration as DeclarationViewer);
  }, [openDraft, declaration]);

  const openInNewTab = useCallback(async () => {
    window.open(link);
    setContextMenu(null);
  }, [link]);

  // Un brouillon ouvert dans un onglet n'est pas supprimé
  const removeLocal = useCallback(async () => {
    setContextMenu(null);
    if (!(await deleteDraftDeclarationAsync(id))) toast.warn(t('qcapp_declaration_opened_elsewhere').toString());
  }, [id, deleteDraftDeclarationAsync, t]);

  const contextMenuClose = useCallback(() => {
    setContextMenu(null);
  }, []);

  const openContextMenu = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();
      setContextMenu(
        contextMenu === null
          ? {
              mouseX: event.clientX - 2,
              mouseY: event.clientY - 4,
            }
          : // repeated contextmenu when it is already open closes it with Chrome 84 on Ubuntu
            // Other native context menus might behave different.
            // With this behavior we prevent contextmenu from the backdrop to re-locale existing context menus.
            null,
      );
    },
    [contextMenu],
  );
  return {
    openDeclaration,
    isMobile,
    openContextMenu,
    contextMenu,
    contextMenuClose,
    openInNewTab,
    removeLocal,
    promptProps,
  };
};

export default useData;
